import React, { useMemo } from 'react';

import { useEntityPreviewData } from '@work4all/components/lib/components/entity-preview/hooks/use-entity-preview-data';
import { TicketPreview } from '@work4all/components/lib/components/entity-preview/ticket-preview/TicketPreview';
import { TicketPreviewFields } from '@work4all/components/lib/components/entity-preview/ticket-preview/TicketPreviewFields';
import { EntityPreviewContainerProps } from '@work4all/components/lib/components/entity-preview/types';

import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { EntityPreviewDataProviderProps } from '../../../../../../components/data-tables/entity-table/types';
import { mergeActions } from '../../../../../../components/data-tables/entity-table/utils';
import { useTicketToTimeTracking } from '../../../../../../hooks/use-ticket-to-time-tracking';
import { EntityTableView } from '../../../../../file-entities-lists/FileEntitiesListsController';
import {
  useYellowNoteContext,
  YellowNoteContext,
} from '../../yellow-note/yellow-note-context';
import { YellowNoteCard } from '../../yellow-note/YellowNoteCard';
import { useTicketCustomActions } from '../use-ticket-custom-actions';

type TicketPreviewContainerProps = EntityPreviewContainerProps<string> & {
  entries?: { id?: string }[];
};

const TimeTrackingTable = ({ ticket }: { ticket: Ticket }) => {
  const init = useTicketToTimeTracking(ticket);
  const presetFields = useMemo(() => {
    return JSON.stringify(init);
  }, [init]);

  return (
    <EntityTableView
      entityType={Entities.ticketTimeTracking}
      prefilter={[
        {
          'ticket.id': { $eq: ticket.id },
        },
      ]}
      tableComponentProps={{
        maskParams: {
          presetFields,
        },
      }}
    />
  );
};

const renderTimeTrackingTable = (ticket: Ticket) => {
  return <TimeTrackingTable ticket={ticket} />;
};

export function TicketPreviewContainer(props: TicketPreviewContainerProps) {
  const { isPartnerPage, entries, ...handlers } = props;
  const tickets = entries as Ticket[];

  if (!tickets) {
    return null;
  }

  const yellowNoteData = tickets[0].businessPartner?.data?.postIt;
  const hasYellowNoteData =
    tickets.length === 1 && yellowNoteData?.body?.length;

  if (isPartnerPage) {
    return (
      <TicketPreviewFromFile
        handlers={handlers}
        tickets={tickets}
        hasYellowNoteData={hasYellowNoteData}
      />
    );
  }

  return (
    <TicketPreviewWithYellowNote
      handlers={handlers}
      tickets={tickets}
      yellowNoteData={yellowNoteData}
      hasYellowNoteData={hasYellowNoteData}
    />
  );
}

export const TicketPreviewDataProvider = (
  props: EntityPreviewDataProviderProps & {
    children: (props: EntityPreviewDataProviderProps) => React.ReactNode;
  }
) => {
  const { children, ...dataProps } = props;

  const { entries, ...others } = dataProps;
  const ids = entries.map((e) => e.id);
  // Extract ids from entries if ids is not provided
  const ticketIds =
    ids ||
    (entries
      ?.map((entry) => entry.id)
      .filter((id) => id !== undefined) as string[]) ||
    [];

  const tickets = useEntityPreviewData<Ticket>(
    ticketIds as string[],
    Entities.ticket,
    TicketPreviewFields
  );

  const { actions, emailPopover } = useTicketCustomActions({
    tickets,
  });

  const custom = useMemo(
    () =>
      mergeActions(props.custom, {
        left: actions,
      }),
    [props.custom, actions]
  );

  if (!tickets) {
    return null;
  }

  return (
    <>
      {emailPopover}
      {children({
        ...others,
        entries: tickets,
        custom,
      })}
    </>
  );
};

function TicketPreviewFromFile({ handlers, tickets, hasYellowNoteData }) {
  const { isOpen } = useYellowNoteContext();
  const hideYellowNote = !hasYellowNoteData || (hasYellowNoteData && isOpen);

  return (
    <TicketPreview
      {...handlers}
      entries={tickets}
      YellowNote={!hideYellowNote ? <YellowNoteCard /> : null}
      renderTimeTrackingTable={renderTimeTrackingTable}
    />
  );
}

function TicketPreviewWithYellowNote({
  handlers,
  tickets,
  yellowNoteData,
  hasYellowNoteData,
}) {
  // Add/Delete actions not allowed for the Ticket' YellowNote
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const asyncNoop = async () => {};

  return (
    <YellowNoteContext
      data={yellowNoteData ?? null}
      updateYellowNote={asyncNoop}
      deleteYellowNote={asyncNoop}
    >
      <TicketPreview
        {...handlers}
        entries={tickets}
        YellowNote={hasYellowNoteData ? <YellowNoteCard /> : null}
        renderTimeTrackingTable={renderTimeTrackingTable}
      />
    </YellowNoteContext>
  );
}
